class dynamicModal extends HTMLElement {
  constructor() {
    super()

    this.body = document.querySelector('body')
    this.swipeBar = this.querySelector('.dynamic-modal__swipe-bar')
    this.isDragging = false
    this.startY = 0
    this.currentY = 0
    this.initialTransform = 0
    this.dragThreshold = 80 // Distance in pixels to trigger modal close
    this.dragStartArea = 200 // Area from top where dragging can start
  }

  connectedCallback() {
    this.bindEvents()
  }

  bindEvents() {
    const overlay = this.querySelector('.dynamic-modal__overlay')
    const closeBtn = this.querySelector('.dynamic-modal__close')

    overlay.addEventListener('click', () => this.close())
    closeBtn.addEventListener('click', () => this.close())

    // Swipe bar events
    if (this.swipeBar) {
      if (window.innerWidth < 768) {
        this.bindSwipeEvents()
      }
    }

    // Close modal with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.close()
      }
    })
  }

  bindSwipeEvents() {
    const container = this.querySelector('.dynamic-modal__container')

    // Touch events for mobile
    container.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false })
    container.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false })
    container.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: false })

    // Mouse events for desktop
    container.addEventListener('mousedown', (e) => this.handleMouseStart(e))
    document.addEventListener('mousemove', (e) => this.handleMouseMove(e))
    document.addEventListener('mouseup', (e) => this.handleMouseEnd(e))
  }

  handleTouchStart(e) {
    // Only allow swipe if the touch starts within `dragStartArea` pixels from
    // the top edge of the modal container (instead of the viewport).
    const container = this.querySelector('.dynamic-modal__container')
    const rect = container?.getBoundingClientRect()
    if (!rect || e.touches[0].clientY - rect.top > this.dragStartArea) return
    this.isDragging = true
    this.startY = e.touches[0].clientY
    this.currentY = this.startY
    this.initialTransform = 0

    container.style.transition = 'none'
  }

  handleTouchMove(e) {
    if (!this.isDragging) return
    e.preventDefault()
    this.currentY = e.touches[0].clientY
    const deltaY = this.currentY - this.startY

    // Only allow downward movement
    if (deltaY > 0) {
      const container = this.querySelector('.dynamic-modal__container')
      const translateY = Math.min(deltaY, 200) // Max drag distance
      container.style.transform = `translateY(${translateY}px)`
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return
    this.isDragging = false
    const deltaY = this.currentY - this.startY
    const container = this.querySelector('.dynamic-modal__container')

    container.style.transition = 'transform 0.3s ease'

    // If dragged down more than threshold, close the modal
    if (deltaY > this.dragThreshold) {
      this.close()
    } else {
      // Reset position
      container.style.transform = 'translateY(0)'
    }
  }

  handleMouseStart(e) {
    // Only allow drag if the mouse press starts within `dragStartArea` pixels
    // from the top edge of the modal container (instead of the viewport).
    const container = this.querySelector('.dynamic-modal__container')
    const rect = container?.getBoundingClientRect()
    if (!rect || e.clientY - rect.top > this.dragStartArea) return
    this.isDragging = true
    this.startY = e.clientY
    this.currentY = this.startY
    this.initialTransform = 0

    container.style.transition = 'none'
  }

  handleMouseMove(e) {
    if (!this.isDragging) return
    this.currentY = e.clientY
    const deltaY = this.currentY - this.startY

    // Only allow downward movement
    if (deltaY > 0) {
      const container = this.querySelector('.dynamic-modal__container')
      const translateY = Math.min(deltaY, 200) // Max drag distance
      container.style.transform = `translateY(${translateY}px)`
    }
  }

  handleMouseEnd(e) {
    if (!this.isDragging) return
    this.isDragging = false
    const deltaY = this.currentY - this.startY
    const container = this.querySelector('.dynamic-modal__container')

    container.style.transition = 'transform 0.3s ease'

    // If dragged down more than threshold, close the modal
    if (deltaY > this.dragThreshold) {
      this.close()
    } else {
      // Reset position
      container.style.transform = 'translateY(0)'
    }
  }

  open() {
    const scrollbarWidth = Math.max(window.innerWidth - document.documentElement.clientWidth, 0)
    this.body.style.setProperty('--dynamic-modal-scrollbar-width', `${scrollbarWidth}px`)
    this.classList.add('active')
    const container = this.querySelector('.dynamic-modal__container')
    if (container) {
      container.style.transform = 'translateY(0)'
    }
    this.body.classList.add('dynamic-modal-open')

    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        detail: { modal: this },
      }),
    )

    const compareModal = this.querySelector('.class-js-compare-modal')
    if (compareModal) {
      const input = this.querySelector(`input[value=compare-${compareModal.dataset.style}]`)
      if (input) {
        input.click()
      }
    }
  }

  close() {
    this.classList.remove('active')

    // Reset transform when closing
    const container = this.querySelector('.dynamic-modal__container')
    if (container) {
      container.style.transform = 'translateY(100%)'
      container.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    }
    this.body.classList.remove('dynamic-modal-open')
    this.body.style.removeProperty('--dynamic-modal-scrollbar-width')

    this.dispatchEvent(
      new CustomEvent('modalClosed', {
        detail: { modal: this },
      }),
    )
  }

  getIsOpen() {
    return this.classList.contains('active')
  }

  // context, e.g. a product or collection url path, that the section is rendered in
  // context = 'products/product-handle' or 'collections/collection-handle'
  async renderSectionWithContext(
    sectionId,
    targetSelector,
    context = '',
    hasRun = false,
    callback,
  ) {
    try {
      if (hasRun) return true
      const destination = this.querySelector('.dynamic-modal__content')
      context = context.startsWith('/') ? context.slice(1) : context
      const url = `${window.Shopify.routes.root}${context}?section_id=${sectionId}`
      if (!destination || !sectionId) throw new Error('destination or sectionId not found')

      const response = await fetch(url)
      if (!response.ok) throw new Error('Network response was not ok')
      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      const target = doc?.querySelector(`${targetSelector}`)
      if (!target) throw new Error('target not found in response')
      this.dispatchEvent(
        new CustomEvent('dynamic-modal-render-section', {
          detail: { sectionId },
        }),
      )
      destination.innerHTML = target.outerHTML

      this.setAttribute('data-section', sectionId)
      if (callback) callback()
      return true
    } catch (error) {
      console.warn('Error in renderSectionWithContext:', error)
    }
  }

  renderDynamicModalImage(config) {
    const {
      modalId,
      imageUrl,
      imageUrlMobile,
      imageHeight,
      imageWidth,
      imageHeightMobile,
      imageWidthMobile,
      backgroundColor,
      imageCaption,
    } = config
    if (!imageUrl) return
    const modalContent = this.querySelector('.dynamic-modal__content')
    const modalContainer = this.querySelector('.dynamic-modal__container')
    if (!modalContent) return

    // Create image element
    const img = document.createElement('img')
    this.classList.add('dynamic-modal-image')

    // Set width and height attributes for better performance
    if (window.innerWidth >= 768) {
      img.width = imageWidth
      img.height = imageHeight
    } else if (imageUrlMobile) {
      img.width = imageWidthMobile
      img.height = imageHeightMobile
    }

    // Set responsive image sources
    if (window.innerWidth >= 768) {
      img.src = imageUrl
      // modalContent.style.aspectRatio = `${imageWidth} / ${imageHeight}`
    } else if (imageUrlMobile) {
      img.src = imageUrlMobile
      // modalContent.style.aspectRatio = `${imageWidthMobile} / ${imageHeightMobile}`
    } else {
      img.src = imageUrl
      // modalContent.style.aspectRatio = `${imageWidth} / ${imageHeight}`
    }

    modalContent.innerHTML = ''
    modalContent.appendChild(img)
    this.setAttribute('data-section', `${modalId}`)
    if (backgroundColor && modalContainer) modalContainer.style.backgroundColor = backgroundColor

    // if imageCaption is provided, add it to the modal content
    if (imageCaption) {
      const caption = document.createElement('div')
      caption.className = 'dynamic-modal__image-caption'
      caption.innerHTML = imageCaption
      modalContent.appendChild(caption)
    }

    this.open()

    // Handle resize events
    this.resizeHandler = () => this.handleModalImageResize(config, img, modalContent)
    window.addEventListener('resize', this.resizeHandler)
    // Clear image when dynamic-modal is rendering a different section
    this.clearImageHandler = () => this.clearDynamicModalImage()
    this.addEventListener('dynamic-modal-render-section', this.clearImageHandler)
  }

  // Handle resize events
  handleModalImageResize(config, img, container) {
    const {
      imageUrl,
      imageUrlMobile,
      imageWidth,
      imageHeight,
      imageWidthMobile,
      imageHeightMobile,
    } = config
    if (window.innerWidth >= 768) {
      img.src = imageUrl
      img.width = imageWidth
      img.height = imageHeight
      // container.style.aspectRatio = `${imageWidth} / ${imageHeight}`
    } else {
      img.src = imageUrlMobile || imageUrl
      img.width = imageWidthMobile
      img.height = imageHeightMobile
      // container.style.aspectRatio = `${imageWidthMobile} / ${imageHeightMobile}`
    }
  }

  clearDynamicModalImage() {
    const modalContent = this.querySelector('.dynamic-modal__content')
    if (!modalContent) return

    // Remove any existing img elements
    const existingImg = modalContent.querySelector('img')
    if (existingImg) {
      existingImg.remove()
    }

    // Clear aspect ratio and remove image class
    // modalContent.style.aspectRatio = ''
    this.classList.remove('dynamic-modal-image')
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
      this.resizeHandler = null
    }
    if (this.clearImageHandler) {
      this.removeEventListener('dynamic-modal-render-section', this.clearImageHandler)
      this.clearImageHandler = null
    }
  }

  async renderDynamicModalIframe(config) {
    const { modalId, iframeUrl } = config

    const modalContent = this.querySelector('.dynamic-modal__content')
    if (!modalContent) return

    modalContent.innerHTML = ''

    try {
      // Fetch the content directly instead of using iframe
      const response = await fetch(iframeUrl)
      const html = await response.text()

      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html

      // Extract all stylesheets and inline styles
      const stylesheets = tempDiv.querySelectorAll('link[rel="stylesheet"]')
      const inlineStyles = tempDiv.querySelectorAll('style')

      // Create a style container for the modal
      const styleContainer = document.createElement('div')
      styleContainer.className = 'dynamic-modal-styles'

      // Add external stylesheets
      stylesheets.forEach((link) => {
        const styleLink = link.cloneNode(true)
        styleContainer.appendChild(styleLink)
      })

      // Add inline styles
      inlineStyles.forEach((style) => {
        const inlineStyle = style.cloneNode(true)
        styleContainer.appendChild(inlineStyle)
      })

      // Find the grid-container
      const gridContainer = tempDiv.querySelector('.grid-container')

      if (gridContainer) {
        // Add styles to modal content first
        modalContent.appendChild(styleContainer)

        // Clone the grid-container content
        const clonedContent = gridContainer.cloneNode(true)

        // Add responsive modal styles to let content grow naturally
        const modalStyles = document.createElement('style')
        modalStyles.textContent = `
          .dynamic-modal__content {
            /* max-height: 80vh !important; */
            max-width: 1000px !important;
            overflow: auto !important;
            width: auto !important;
            height: auto !important;
          }
          .dynamic-modal__content .grid-container {
            max-width: 100% !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            width: auto !important;
            height: auto !important;
          }
          .dynamic-modal__content .grid-container * {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            white-space: normal !important;
          }
          .dynamic-modal__content .grid-container p,
          .dynamic-modal__content .grid-container div,
          .dynamic-modal__content .grid-container span {
            word-break: break-word !important;
            hyphens: auto !important;
          }
          .dynamic-modal__content svg.brand-logo {
            margin: 20px 0 !important;
          }
          @media (max-width: 400px) {
            div.row-0 {
              overflow-x: hidden !important;
            }
          }
          .dynamic-modal__content .grid-container {
            padding-bottom: 2rem !important;
          }
        `
        modalContent.appendChild(modalStyles)
        modalContent.appendChild(clonedContent)
        this.setAttribute('data-section', `${modalId}`)

        this.open()
      } else {
        console.warn('Grid container not found in fetched content')
      }
    } catch (error) {
      console.error('Error fetching content:', error)
    }
  }

  // Static method to get or create modal instance
  static getInstance() {
    let modal = document.querySelector('dynamic-modal')
    if (!modal) {
      modal = document.createElement('dynamic-modal')
      document.body.appendChild(modal)
    }
    return modal
  }
}
if (!customElements.get('dynamic-modal')) {
  customElements.define('dynamic-modal', dynamicModal)
}

// Method to initialize Okendo widget
async function initializeOkendoModal(context = '') {
  const sectionId = 'okendo-dynamic'
  const targetSelector = 'okendo-reviews'
  const callback = () => {}
  const modal = document.querySelector('dynamic-modal')
  if (!modal) return console.error('initializeOkendoModal - modal not found')
  await modal.renderSectionWithContext(sectionId, targetSelector, context, false, callback)
  const okendoWidget = modal.querySelector('okendo-reviews [data-oke-widget]')
  if (!okendoWidget) return console.warn('initializeOkendoModal - okendoWidget not found')
  await initializeOkendoWidget(okendoWidget)
}

async function renderOkendoReviewsModal(productHandle, productId) {
  const modal = document.querySelector('dynamic-modal')
  if (!modal) return console.error('renderOkendoReviewsModal - no modal found')
  const okendoWidget = modal.querySelector('dynamic-modal okendo-reviews [data-oke-widget]')
  if (productHandle) {
    // if the dynamic-modal is not showing the okendo-widget, call the initialize function.
    await initializeOkendoModal(`products/${productHandle}`)
    return modal.open()
  } else if (okendoWidget && productId) {
    const currentOkendoProductId = okendoWidget.dataset.okeReviewsProductId
    if (currentOkendoProductId?.includes(productId)) return modal.open()
    await renderOkendoReviews(okendoWidget, productId)
    modal.open()
  } else if (!okendoWidget && productId) {
    // if the dynamic-modal is not showing the okendo-widget, call the initialize function.
    await initializeOkendoModal()
    const okendoWidget = document.querySelector('dynamic-modal okendo-reviews [data-oke-widget]')
    if (!okendoWidget)
      console.error('renderOkendoReviewsModal - no okendoWidget found after init - modal: ', modal)
    const currentOkendoProductId = okendoWidget.dataset.okeReviewsProductId
    if (currentOkendoProductId?.includes(productId)) return modal.open()
    setTimeout(async () => {
      await renderOkendoReviews(okendoWidget, productId)
      modal.open()
    }, 0)
  } else {
    console.error(
      `renderOkendoReviewsModal - error - no handle: ${productHandle} or id: ${productId}`,
    )
  }
}

// Add click event listener to star rating elements and render Okendo widget
async function reviewStarsClickHandler(e) {
  const reviewStars = e.target.closest('.js-product-star-rating')
  if (!reviewStars) return

  e.preventDefault()
  e.stopPropagation()
  const modal = document.querySelector('dynamic-modal')
  const productContext =
    reviewStars.closest('[data-product-id], [data-product-form], product-info') ||
    document.querySelector('[data-product-id], [data-product-form], product-info')
  const productId =
    productContext?.dataset.productId ||
    productContext?.querySelector('[data-product-json][data-product-id]')?.dataset.productId ||
    document.querySelector('[data-product-json][data-product-id]')?.dataset.productId
  const okendoWidget = modal?.querySelector('dynamic-modal okendo-reviews [data-oke-widget]')

  if (!modal || !productId) return console.error('Dynamic Modal - no modal or productId found')
  if (!okendoWidget) {
    // if the dynamic-modal is not showing the okendo-widget, call the initialize function.
    // console.error('Dynamic Modal - no okendoWidget found')
    const productHandle =
      productContext?.dataset.productHandle ||
      reviewStars.closest('[data-product-handle]')?.dataset.productHandle ||
      document.querySelector('[data-product-handle]')?.dataset.productHandle
    if (!productHandle) return console.error('Dynamic Modal - no productHandle found')
    await initializeOkendoModal(`products/${productHandle}`)
    return modal.open()
  }
  const currentOkendoProductId = okendoWidget.dataset.okeReviewsProductId
  if (currentOkendoProductId?.includes(productId)) {
    return modal.open()
  }
  await renderOkendoReviews(okendoWidget, productId)
  modal.open()
  // } else {
  //   document
  //     .querySelector('.section-pdp-app-okendo-dynamic [data-oke-widget]')
  //     ?.scrollIntoView({ behavior: 'smooth' })
  // }
}

document.addEventListener('DOMContentLoaded', function () {
  const modal = dynamicModal.getInstance()
  if (!modal) return console.error('modal not found')

  initializeOkendoModal()

  // Add click event listener to star rating elements and render Okendo widget
  document.addEventListener('click', reviewStarsClickHandler)
})
